/**
 * SharedDatenbankTab — AP-scoped database tab.
 *
 * Reads the current AP from APContext to determine which scopes to
 * show (visibleScopeIds). Empty visibleScopeIds = all scopes (used
 * by Configuration AP, preserving its full behaviour).
 *
 * v6: Monitor APs add a flag overlay (idle) or evidence overlay (review) above
 * the pivot table.
 *
 * Rendering cases:
 *  1. DB configured → scoped scope-tabs + PivotTable with diff overlay
 *  2. Empty DB + active diff → transient message
 *  3. Empty DB, no diff → placeholder
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconAlertTriangle,
  IconPackage,
  IconPuzzle,
  IconTable,
  IconTool,
  IconUsers,
} from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import { getScopeDisplayName } from "../../config/scopeIcons"
import { SelectionBadge } from "../../layout/SelectionBadge"
import { PivotTable } from "../../pivot/PivotTable"
import { useAP } from "./APContext"

const SCOPE_ICONS: Record<string, typeof IconTool> = {
  supplier: IconTool,
  component: IconPuzzle,
  product: IconPackage,
  customer: IconUsers,
  substance: IconAlertTriangle,
}

function MonitorFlagOverlay({ apId }: { apId: string }) {
  const { translate } = useTranslation()
  const { data: findings = [] } = useQuery({
    queryKey: ["monitor-findings-open", apId],
    queryFn: () => api.monitorListFindings(apId, "open"),
    staleTime: 30_000,
  })
  if (findings.length === 0) return null
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-yellow1/30 bg-yellow1/10 px-4 py-2">
      <span className="text-xs font-medium text-yellow1">
        🚩 {translate("QMS/DATABASE/OPEN_FINDINGS_LABEL")}:
      </span>
      {findings.slice(0, 5).map((f) => (
        <span
          key={f.id}
          className="rounded bg-yellow1/20 px-1.5 py-0.5 text-xs text-yellow1"
        >
          {f.summary.slice(0, 40)}
          {f.summary.length > 40 ? "…" : ""}
        </span>
      ))}
      {findings.length > 5 && (
        <span className="text-xs text-yellow1">
          +{findings.length - 5} {translate("QMS/DATABASE/MORE_SUFFIX")}
        </span>
      )}
    </div>
  )
}

function MonitorEvidenceOverlay({
  apId,
  findingId,
}: {
  apId: string
  findingId: string
}) {
  const { translate } = useTranslation()
  const { data: finding } = useQuery({
    queryKey: ["monitor-finding", apId, findingId],
    queryFn: () => api.monitorGetFinding(apId, findingId),
    staleTime: 5_000,
  })
  if (!finding) return null
  return (
    <div className="shrink-0 space-y-0.5 border-b border-grey4 bg-grey1 px-4 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-yellow1">
          {translate("QMS/AUTOPILOT/FINDING_VIEW")}
        </span>
        <span className="truncate text-xs text-grey5">{finding.summary}</span>
      </div>
      {finding.evidence.citation && (
        <p className="truncate text-xs italic text-grey5">
          "{finding.evidence.citation}"
        </p>
      )}
      <p className="text-[10px] text-grey5">
        {translate("QMS/MONITOR/RECORD_LABEL")}:{" "}
        {finding.flagged_record_id.slice(0, 12)}… ·{" "}
        {translate("QMS/DATABASE/READ_ONLY_VIEW")}
      </p>
    </div>
  )
}

export function SharedDatenbankTab() {
  const { translate, currentLanguage } = useTranslation()
  const ap = useAP()
  const {
    dbConfigured,
    scopes,
    activeScopeId,
    setActiveScopeId,
    hiddenScopes,
    activeDiffSessionId,
    activeBucketRecordId,
    actionState,
    activeExportRecordId,
    monitorState,
  } = useStore()

  if (dbConfigured) {
    const allVisible = scopes.filter((s) => !hiddenScopes.has(s.id))
    const effectiveScopes =
      ap.scope.visibleScopeIds.length === 0
        ? allVisible
        : allVisible.filter((s) => ap.scope.visibleScopeIds.includes(s.id))

    // Importer-AP idle state: the Datenbank tab IS the selection workspace.
    const isImporterIdle =
      ap.apType === "importer" && !activeBucketRecordId && !actionState
    // Exporter-AP idle state: same rationale.
    const isExporterIdle =
      ap.apType === "exporter" && !activeExportRecordId && !actionState
    const forceSelectMode =
      (isImporterIdle || isExporterIdle) &&
      activeScopeId === ap.scope.primaryScopeId

    // Monitor-AP overlays (v6)
    const isMonitor = ap.apType === "monitor"
    const monitorApId = isMonitor ? ap.id : null
    const activeFindingId =
      monitorState?.apId === ap.id ? monitorState.activeFindingId : null

    return (
      <div className="flex h-full flex-col overflow-hidden">
        {/* Monitor overlays */}
        {isMonitor && monitorApId && !activeFindingId && (
          <MonitorFlagOverlay apId={monitorApId} />
        )}
        {isMonitor && monitorApId && activeFindingId && (
          <MonitorEvidenceOverlay
            apId={monitorApId}
            findingId={activeFindingId}
          />
        )}

        {effectiveScopes.length > 0 && (
          <div className="flex items-end gap-0.5 border-b border-grey4 px-4">
            {effectiveScopes.map((scope) => {
              const Icon = SCOPE_ICONS[scope.id] || IconTable
              return (
                <button
                  key={scope.id}
                  onClick={() => setActiveScopeId(scope.id)}
                  className={clsx(
                    "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                    activeScopeId === scope.id
                      ? "border-yellow1 text-yellow1"
                      : "border-transparent text-grey5 hover:border-grey5 hover:text-white",
                  )}
                >
                  <Icon size={16} />
                  {getScopeDisplayName(scope, currentLanguage)}
                </button>
              )
            })}
          </div>
        )}
        <SelectionBadge />
        <div className="flex-1 overflow-hidden">
          <PivotTable
            key={activeScopeId}
            scopeId={activeScopeId}
            forceSelectMode={!isMonitor && forceSelectMode}
            readOnly={isMonitor && !!activeFindingId}
          />
        </div>
      </div>
    )
  }

  if (activeDiffSessionId) {
    return (
      <div className="flex h-full items-center justify-center bg-grey2">
        <p className="max-w-md px-6 text-center text-sm text-grey5">
          {translate("QMS/DATABASE/PREPARING")}
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full items-center justify-center bg-grey2">
      <p className="max-w-md px-6 text-center text-sm text-grey5">
        {translate("QMS/DATABASE/EMPTY_UPLOAD")}
      </p>
    </div>
  )
}
